import { useCallback, useEffect, useRef, useState } from 'react'
import type { Status, TaskRow } from '../lib/api'
import type { Headline } from '../lib/headlinesApi'
import type { Issue } from '../lib/issuesApi'
import { shopDateTime } from '../lib/grouping'
import {
  getCurrentMeeting,
  listMeetings,
  rateMeeting,
  setMeetingSection,
  startMeeting,
  type Meeting as MeetingRow,
  type MeetingRecap,
  type MeetingSummary,
} from '../lib/meetingApi'
import {
  agendaItem,
  issuePrefillFromTask,
  taskPrefillFromHeadline,
  taskPrefillFromIssue,
  type SectionKey,
  type TaskRef,
} from '../lib/meeting'
import type { TaskPrefill } from './AdminTasks'
import Issues, { type IssuePrefill } from './Issues'
import Headlines from './Headlines'
import Scorecard from './Scorecard'
import MeetingBar from './MeetingBar'
import MeetingTodos from './MeetingTodos'
import MeetingConclude, { RatingGrid, Recap } from './MeetingConclude'

interface Props {
  token: string
  isAdmin: boolean
  meId: string | null
  tasks: TaskRow[]
  busy: Set<string>
  onStatus: (assignmentId: string, status: Status) => void
  onNote: (assignmentId: string, note: string) => void
  onTasksChanged: () => void
}

/** An hour after the end, people can still rate — same rule as the database. */
const RATING_GRACE_MS = 60 * 60 * 1000

/** The parts of a recap that mean "the presenter did something" when they change. */
function recapSignature(r: MeetingRecap): string {
  return [r.headlines_shared, r.issues_solved, r.issues_reported, r.issues_archived, r.tasks_created].join('/')
}

function ratedLabel(avg: number | null, count: number): string {
  if (count === 0 || avg == null) return 'not rated yet'
  return `rated ${avg}`
}

/**
 * Meeting mode — the Shop L10, run from the app.
 *
 * The presenter (an admin) starts it, steps through the six agenda sections
 * and ends it. Everyone else's phone POLLS `get_current_meeting` and follows:
 * same section, same countdown, same recap. The timer is derived from the
 * server's timestamps plus a measured clock skew, so it survives a reload
 * and every device shows the presenter's clock.
 *
 * Each section embeds the real tab component (Scorecard, Headlines, Issues,
 * the admin task list) rather than a copy, so marking a headline shared or
 * solving an issue in the meeting is the same action as on the tab — which
 * is also why the recap can be a time window over existing timestamps.
 *
 * "Create a task" / "Create an issue" inside the meeting never leave it: they set
 * a DETOUR that swaps the content pane locally (the server's section is
 * untouched, so nobody else's phone jumps), with a banner and a Back button,
 * and clear when the form closes.
 */
export default function Meeting({
  token,
  isAdmin,
  meId,
  tasks,
  busy,
  onStatus,
  onNote,
  onTasksChanged,
}: Props) {
  // undefined = not loaded yet; null = no meeting running.
  const [meeting, setMeeting] = useState<MeetingRow | null | undefined>(undefined)
  const [history, setHistory] = useState<MeetingSummary[]>([])
  const [skewMs, setSkewMs] = useState(0)
  const [refreshKey, setRefreshKey] = useState(0)
  const [detour, setDetour] = useState<'todos' | 'ids' | null>(null)
  // What the detour banner says — set with the detour, since four different
  // hand-offs now share the same two panes.
  const [detourNote, setDetourNote] = useState('')
  const [taskPrefill, setTaskPrefill] = useState<TaskPrefill | null>(null)
  const [issuePrefill, setIssuePrefill] = useState<IssuePrefill | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [problem, setProblem] = useState<string | null>(null)
  const [acting, setActing] = useState(false)
  const [justEnded, setJustEnded] = useState<MeetingSummary | null>(null)
  const [pastOpen, setPastOpen] = useState(false)
  // What the previous poll saw, for spotting the presenter's actions.
  const prevRef = useRef<MeetingRow | null | undefined>(undefined)

  const loadHistory = useCallback(async () => {
    try {
      const rows = await listMeetings(token)
      setHistory(rows)
      return rows
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
      return []
    }
  }, [token])

  const poll = useCallback(async () => {
    try {
      const m = await getCurrentMeeting(token)
      if (m) setSkewMs(Date.now() - Date.parse(m.server_now))
      const prev = prevRef.current
      if (m && prev) {
        // Only when the presenter acted — not on every tick — so the embedded
        // lists re-fetch when something changed, not every ten seconds.
        if (
          m.current_section !== prev.current_section ||
          recapSignature(m.recap) !== recapSignature(prev.recap)
        ) {
          setRefreshKey((k) => k + 1)
        }
      }
      if (!m && prev) {
        // The presenter concluded. Show the just-frozen recap.
        const rows = await loadHistory()
        setJustEnded(rows[0] ?? null)
        setDetour(null)
      }
      prevRef.current = m
      setMeeting(m)
      setProblem(null)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
      if (prevRef.current === undefined) setMeeting(null)
    }
  }, [token, loadHistory])

  // Poll: 10s while a meeting is open, 20s while none. Paused while the tab
  // is hidden — except for the very first poll, which always runs, or a tab
  // opened in the background would sit on "Loading…" until it was shown.
  // Polls straight away on becoming visible again.
  useEffect(() => {
    let timer: number | undefined
    let cancelled = false
    let first = true
    const tick = async () => {
      if (cancelled) return
      if (first || document.visibilityState !== 'hidden') await poll()
      first = false
      if (cancelled) return
      timer = window.setTimeout(() => void tick(), prevRef.current ? 10_000 : 20_000)
    }
    void tick()
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        window.clearTimeout(timer)
        void tick()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [poll])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  // One-second clock while a meeting is open.
  const open = !!meeting
  useEffect(() => {
    if (!open) return
    const id = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [open])

  /** An admin action, then an immediate poll so this device updates first. */
  const act = async (fn: () => Promise<void>) => {
    setActing(true)
    setProblem(null)
    try {
      await fn()
      await poll()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setActing(false)
    }
  }

  const start = () =>
    act(async () => {
      await startMeeting(token)
      setJustEnded(null)
    })

  const goSection = (section: number) => {
    if (!meeting) return
    const id = meeting.id
    void act(async () => {
      await setMeetingSection(token, id, section)
      setDetour(null)
    })
  }

  // --- Detours: the create-and-link handoffs, kept inside the meeting so the
  // shop never loses its place. App's global prefill state is not involved.
  //
  //   issue    → task   (IDS)        "Create a task"
  //   headline → issue  (Headlines)  "Create an issue"
  //   headline → task   (Headlines)  "Create a task"
  //   to-do    → issue  (To-Dos)     "Create an issue" — the Ninety linked
  //                                  issue: someone hit a problem with a to-do,
  //                                  it is raised on the spot and waits in IDS
  //
  // Each swaps the content pane for the one form and, when the form closes,
  // drops back to the section the presenter is on.
  const makeTaskHere = useCallback((issue: Issue) => {
    setTaskPrefill(taskPrefillFromIssue(issue))
    setDetourNote('Creating a task from the issue — back to IDS when you’re done.')
    setDetour('todos')
  }, [])
  const makeIssueHere = useCallback((h: Headline) => {
    setIssuePrefill({ headlineId: h.id, taskId: null, description: h.body })
    setDetourNote('Creating an issue from the headline — back to Headlines when you’re done.')
    setDetour('ids')
  }, [])
  const createTaskHere = useCallback((h: Headline) => {
    setTaskPrefill(taskPrefillFromHeadline(h))
    setDetourNote('Creating a task from the headline — back to Headlines when you’re done.')
    setDetour('todos')
  }, [])
  const createIssueHere = useCallback((task: TaskRef) => {
    setIssuePrefill(issuePrefillFromTask(task))
    setDetourNote('Creating an issue from the to-do — back to To-Dos when you’re done.')
    setDetour('ids')
  }, [])
  const clearTaskPrefill = useCallback(() => setTaskPrefill(null), [])
  const clearIssuePrefill = useCallback(() => setIssuePrefill(null), [])
  const endDetour = useCallback(() => {
    setDetour(null)
    setRefreshKey((k) => k + 1)
  }, [])
  const noop = useCallback(() => {}, [])

  const serverNow = nowMs - skewMs

  // ------------------------------------------------------------- no meeting
  if (meeting === undefined) return <p className="muted">Loading…</p>

  if (meeting === null) {
    const last = history[0]
    const past = history.slice(1)
    return (
      <section>
        {problem && <p className="banner">{problem}</p>}

        {justEnded && (
          <p className="banner banner--ok">
            Meeting ended · {justEnded.duration_minutes} min ·{' '}
            {ratedLabel(justEnded.rating_avg, justEnded.rating_count)}
          </p>
        )}

        <div className="panel panel--flat">
          <h2 className="panel__title">No meeting running</h2>
          <p className="muted">
            The Shop L10 runs from here. When it starts, this page follows along —
            the section, the clock and what gets decided.
          </p>
          {isAdmin && (
            <button type="button" className="btn" disabled={acting} onClick={() => void start()}>
              {acting ? 'Starting…' : 'Start Shop L10'}
            </button>
          )}
        </div>

        {last && (
          <LastMeeting
            summary={last}
            token={token}
            serverNow={serverNow}
            onRated={() => void loadHistory()}
          />
        )}

        {past.length > 0 && (
          <div className="fold">
            <button
              type="button"
              className="fold__head"
              aria-expanded={pastOpen}
              onClick={() => setPastOpen((o) => !o)}
            >
              <span className="fold__caret" aria-hidden="true">
                {pastOpen ? '▾' : '▸'}
              </span>
              Past meetings
              <span className="group__count">{past.length}</span>
            </button>
            {pastOpen && (
              <ul className="mini fold__body">
                {past.map((m) => (
                  <li key={m.id} className="mini__row">
                    <span className="mini__title">{shopDateTime(m.started_at)}</span>
                    <span className="mini__meta">
                      <span>{m.duration_minutes} min</span>
                      <span>{ratedLabel(m.rating_avg, m.rating_count)}</span>
                      <span>{m.headlines_shared} headlines</span>
                      <span>{m.issues_solved} solved</span>
                      <span>{m.tasks_created} to-dos</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>
    )
  }

  // --------------------------------------------------------------- running
  const sectionKey: SectionKey = detour ?? agendaItem(meeting.current_section).key

  let content: React.ReactNode
  switch (sectionKey) {
    case 'segue':
      content = (
        <div className="panel panel--flat">
          <h2 className="panel__title">Segue</h2>
          <p className="muted">Good news, personal and professional — one from each person.</p>
        </div>
      )
      break
    case 'scorecard':
      content = <Scorecard token={token} isAdmin={isAdmin} />
      break
    case 'todos':
      content = (
        <MeetingTodos
          token={token}
          isAdmin={isAdmin}
          meId={meId}
          tasks={tasks}
          busy={busy}
          onStatus={onStatus}
          onNote={onNote}
          onTasksChanged={onTasksChanged}
          prefill={taskPrefill}
          onPrefillUsed={clearTaskPrefill}
          onCreateIssue={createIssueHere}
          onFormClosed={detour ? endDetour : noop}
          refreshKey={refreshKey}
        />
      )
      break
    case 'headlines':
      content = (
        <Headlines
          token={token}
          isAdmin={isAdmin}
          onMakeIssue={makeIssueHere}
          onCreateTask={createTaskHere}
          refreshKey={refreshKey}
          compact
        />
      )
      break
    case 'ids':
      content = (
        <Issues
          token={token}
          isAdmin={isAdmin}
          startNew={false}
          onStartNewHandled={noop}
          prefill={issuePrefill}
          onPrefillUsed={clearIssuePrefill}
          onMakeTask={makeTaskHere}
          onFormClosed={detour ? endDetour : noop}
          refreshKey={refreshKey}
          hideSolved
          compact
        />
      )
      break
    case 'conclude':
      content = (
        <MeetingConclude
          meeting={meeting}
          token={token}
          isAdmin={isAdmin}
          onChanged={() => void poll()}
        />
      )
      break
  }

  return (
    <section>
      <MeetingBar
        meeting={meeting}
        nowMs={serverNow}
        isAdmin={isAdmin}
        busy={acting}
        onSection={goSection}
      />

      {problem && <p className="banner">{problem}</p>}

      {detour && (
        <div className="banner banner--ok meeting-detour">
          <span>{detourNote}</span>
          <button type="button" className="btn btn--quiet" onClick={endDetour}>
            Back
          </button>
        </div>
      )}

      {content}
    </section>
  )
}

/**
 * The last meeting's frozen recap, with a late-rating control while the
 * hour's grace is still open. Ratings read live (people are still rating),
 * everything else from the frozen recap.
 */
function LastMeeting({
  summary,
  token,
  serverNow,
  onRated,
}: {
  summary: MeetingSummary
  token: string
  serverNow: number
  onRated: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const graceOpen = serverNow <= Date.parse(summary.ended_at) + RATING_GRACE_MS

  const rate = async (n: number) => {
    setBusy(true)
    setProblem(null)
    try {
      await rateMeeting(token, summary.id, n)
      onRated()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const recap: MeetingRecap | null = summary.recap
    ? { ...summary.recap, rating_avg: summary.rating_avg, rating_count: summary.rating_count }
    : null

  return (
    <div className="group">
      <h2 className="group__label">Last meeting</h2>
      <article className="card">
        <div className="card__head">
          <h3 className="card__title">{shopDateTime(summary.started_at)}</h3>
        </div>
        <p className="card__due">
          {summary.duration_minutes} min · started by {summary.started_by_name} ·{' '}
          {ratedLabel(summary.rating_avg, summary.rating_count)}
        </p>
        {recap && <Recap recap={recap} />}
        {problem && <p className="banner banner--tight">{problem}</p>}
        {graceOpen ? (
          <>
            <p className="muted meeting-conclude__lead">
              {summary.my_rating != null ? `You rated it ${summary.my_rating}.` : 'Not rated it yet?'}{' '}
              You can still rate it for an hour after the end.
            </p>
            <RatingGrid value={summary.my_rating} busy={busy} onPick={(n) => void rate(n)} />
          </>
        ) : (
          summary.my_rating != null && (
            <p className="card__due">You rated it {summary.my_rating}.</p>
          )
        )}
      </article>
    </div>
  )
}
